import * as ngCore from '@angular/core'
import * as ngMatDatepicker from '@angular/material/datepicker'
import { Range } from '@attain-apps/range'

import * as config from '../../../../config'
import * as input from '../input'
import * as models from '../models'
import * as overlay from '../overlay'
import { Store } from '../store'

/**
 * @example
 * ```html
 * <atn-ds-date-range-single
 *  label="Some label"
 *  [(value)]="dateRange"
 * />
 * ```
 */
@ngCore.Component({
  imports: [overlay.Component, input.Component],
  providers: [
    Store,
    ...config.dateFormat.DATE_MATERIAL_PROVIDERS,
    // Override Angular Material's default date range selection strategy with a
    // custom implementation defined in `overlay.Component`.
    //
    // This allows us to customize how date ranges are calculated when users
    // interact with the calendar (e.g., controlling how start/end dates are
    // selected, applying logic for custom input fields, etc.).
    {
      provide: ngMatDatepicker.MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: overlay.Component,
    },
  ],
  selector: 'atn-ds-date-range-single',
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link Store} */
  public readonly store = ngCore.inject(Store)

  /** Label text displayed above the date range picker input. */
  public readonly labelSg = ngCore.input<string | undefined>(undefined, {
    alias: 'label',
  })

  /** Holds the current date range input value. */
  public readonly valueSg = ngCore.model<Range<Date> | undefined>(undefined, {
    alias: 'value',
  })

  /** Converts the date-only value to local dates for Angular Material. */
  public readonly displayValueSg = ngCore.computed((value = this.valueSg()) =>
    models.DateRange.normalizeToLocal(value),
  )

  constructor() {
    ngCore.effect(() => {
      const dateRange = this.displayValueSg()
      this.store.patchState({
        selectedDateRange: models.DateRange.generate({
          end: dateRange?.end,
          start: dateRange?.start,
        }),
      })
    })
  }

  /**
   *  Opens the date range picker and initializes it with the selected range
   */
  public openDateRangePickerHandler(
    picker: ngMatDatepicker.MatDateRangePicker<Date>,
  ) {
    this.store.startEditing()
    picker?.open()
  }

  /** Output currently edited range */
  public applyChangesHandler() {
    this.store.finishEditing()
    const { end, start } = this.store.selectedDateRangeSg()
    const range = new Range<Date>({
      end: end ?? undefined,
      start: start ?? undefined,
    })
    this.valueSg.set(models.DateRange.normalizeToUtc(range))
  }

  /** Reset active selection */
  public discardChangesHandler() {
    this.store.discardEditing()
  }
}
